import type { SubscriptionTier, FilamentTrackingMode } from '../types/entities.js';

export interface TierLimits {
  printers: number;
  filaments: number;
  teamMembers: number;
  storageMB: number; // Total storage in megabytes
  hasProjects: boolean;
  hasCostTracking: boolean;
  filamentTracking: FilamentTrackingMode;
}

export const TIER_LIMITS: Record<SubscriptionTier, TierLimits> = {
  TINKERER: {
    printers: 1,
    filaments: 12,
    teamMembers: 1,
    storageMB: 1024, // 1 GB storage
    hasProjects: false,
    hasCostTracking: false,
    filamentTracking: 'catalog',
  },
  HOBBYIST: {
    printers: 5,
    filaments: 50,
    teamMembers: 3,
    storageMB: 5120, // 5 GB storage
    hasProjects: true,
    hasCostTracking: false,
    filamentTracking: 'estimates',
  },
  BUSINESS: {
    printers: Infinity,
    filaments: Infinity,
    teamMembers: Infinity,
    storageMB: 25600, // 25 GB storage
    hasProjects: true,
    hasCostTracking: true,
    filamentTracking: 'full',
  },
} as const;

/**
 * Numeric resource types that have limits
 */
export type LimitableResource = 'printers' | 'filaments' | 'teamMembers';

/**
 * Tenant override fields (null means use tier default)
 */
export interface TenantOverrides {
  printerLimitOverride?: number | null;
  filamentLimitOverride?: number | null;
  spoolLimitOverride?: number | null;
  teamMemberLimitOverride?: number | null;
  modelLimitOverride?: number | null;
}

/**
 * Map resource to override field name
 */
const OVERRIDE_FIELD_MAP: Record<LimitableResource, keyof TenantOverrides> = {
  printers: 'printerLimitOverride',
  filaments: 'filamentLimitOverride',
  teamMembers: 'teamMemberLimitOverride',
};

/**
 * Get the effective limit for a resource, considering tenant overrides
 */
export function getEffectiveLimit(
  tier: SubscriptionTier,
  resource: LimitableResource,
  overrides?: TenantOverrides | null
): number {
  const overrideField = OVERRIDE_FIELD_MAP[resource];
  const overrideValue = overrides?.[overrideField];

  // If override is set (not null/undefined), use it
  if (overrideValue != null) {
    return overrideValue;
  }

  // Otherwise use tier default
  return TIER_LIMITS[tier][resource];
}

/**
 * Get effective printer limit
 */
export function getEffectivePrinterLimit(
  tier: SubscriptionTier,
  overrides?: TenantOverrides | null
): number {
  return getEffectiveLimit(tier, 'printers', overrides);
}

/**
 * Get effective filament limit
 */
export function getEffectiveFilamentLimit(
  tier: SubscriptionTier,
  overrides?: TenantOverrides | null
): number {
  return getEffectiveLimit(tier, 'filaments', overrides);
}

/**
 * Get effective team member limit
 */
export function getEffectiveTeamMemberLimit(
  tier: SubscriptionTier,
  overrides?: TenantOverrides | null
): number {
  return getEffectiveLimit(tier, 'teamMembers', overrides);
}

/**
 * Check if a tier has reached its limit for a resource
 */
export function isAtLimit(
  tier: SubscriptionTier,
  resource: LimitableResource,
  currentCount: number,
  overrides?: TenantOverrides | null
): boolean {
  const limit = getEffectiveLimit(tier, resource, overrides);
  return currentCount >= limit;
}

/**
 * Get remaining capacity for a resource
 */
export function getRemainingCapacity(
  tier: SubscriptionTier,
  resource: LimitableResource,
  currentCount: number,
  overrides?: TenantOverrides | null
): number {
  const limit = getEffectiveLimit(tier, resource, overrides);
  if (limit === Infinity) return Infinity;
  return Math.max(0, limit - currentCount);
}

/**
 * Check if a feature is available for a tier
 */
export function hasFeature(
  tier: SubscriptionTier,
  feature: 'hasProjects' | 'hasCostTracking'
): boolean {
  return TIER_LIMITS[tier][feature];
}

/**
 * Get the filament tracking mode for a tier
 */
export function getFilamentTrackingMode(tier: SubscriptionTier): FilamentTrackingMode {
  return TIER_LIMITS[tier].filamentTracking;
}

/**
 * Get the storage limit in MB for a tier
 */
export function getStorageLimitMB(tier: SubscriptionTier): number {
  return TIER_LIMITS[tier].storageMB;
}
